import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { MoveUpRight, Circle as CircleIcon, Undo2, Eraser } from "lucide-react";

const COLORS = ["#ef4444", "#facc15", "#ffffff"];
const MAX_DIM = 1600;

/* Draw an arrow or circle on a task photo to point at the exact problem spot.
 * Loads the stored photo via the authenticated files endpoint, lets the user
 * drag shapes on a canvas, and saves the annotated copy as a NEW photo (the
 * original is kept) via the regular upload pipeline. */
export default function PhotoMarkup({ path, yachtId, folder, onSaved, onClose }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const draftRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState(COLORS[0]);
  const [shapes, setShapes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setShapes([]); setReady(false); setTool("arrow"); setColor(COLORS[0]);
    if (!path) return;
    let alive = true, blobUrl;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!alive) return;
        blobUrl = URL.createObjectURL(r.data);
        const img = new Image();
        img.onload = () => {
          if (!alive) return;
          imgRef.current = img;
          setReady(true);
        };
        img.src = blobUrl;
      } catch (e) {
        toast.error(fmtErr(e));
        onClose?.();
      }
    })();
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  const drawShape = (ctx, s, w) => {
    ctx.strokeStyle = s.color;
    ctx.lineWidth = Math.max(4, w * 0.008);
    ctx.lineCap = "round";
    if (s.tool === "circle") {
      ctx.beginPath();
      ctx.ellipse((s.x1 + s.x2) / 2, (s.y1 + s.y2) / 2,
        Math.max(Math.abs(s.x2 - s.x1) / 2, 4), Math.max(Math.abs(s.y2 - s.y1) / 2, 4), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      const head = Math.max(16, w * 0.03);
      const ang = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
      ctx.beginPath();
      ctx.moveTo(s.x1, s.y1);
      ctx.lineTo(s.x2, s.y2);
      ctx.moveTo(s.x2, s.y2);
      ctx.lineTo(s.x2 - head * Math.cos(ang - Math.PI / 6), s.y2 - head * Math.sin(ang - Math.PI / 6));
      ctx.moveTo(s.x2, s.y2);
      ctx.lineTo(s.x2 - head * Math.cos(ang + Math.PI / 6), s.y2 - head * Math.sin(ang + Math.PI / 6));
      ctx.stroke();
    }
  };

  const redraw = (draft) => {
    const canvas = canvasRef.current, img = imgRef.current;
    if (!canvas || !img) return;
    const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    [...shapes, ...(draft ? [draft] : [])].forEach((s) => drawShape(ctx, s, canvas.width));
  };

  useEffect(() => { if (ready) redraw(); }, [ready, shapes]); // eslint-disable-line react-hooks/exhaustive-deps

  const coords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvasRef.current.width,
      y: ((e.clientY - rect.top) / rect.height) * canvasRef.current.height,
    };
  };

  const onPointerDown = (e) => {
    if (!ready) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const { x, y } = coords(e);
    draftRef.current = { tool, color, x1: x, y1: y, x2: x, y2: y };
  };
  const onPointerMove = (e) => {
    if (!draftRef.current) return;
    const { x, y } = coords(e);
    draftRef.current = { ...draftRef.current, x2: x, y2: y };
    redraw(draftRef.current);
  };
  const onPointerUp = () => {
    const d = draftRef.current;
    draftRef.current = null;
    if (!d) return;
    if (Math.abs(d.x2 - d.x1) < 6 && Math.abs(d.y2 - d.y1) < 6) { redraw(); return; }
    setShapes((s) => [...s, d]);
  };

  const save = () => {
    if (!shapes.length || saving) return;
    setSaving(true);
    canvasRef.current.toBlob(async (blob) => {
      try {
        const fd = new FormData();
        fd.append("file", new File([blob], "markup.jpg", { type: "image/jpeg" }));
        fd.append("yacht_id", yachtId);
        fd.append("folder", folder);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onSaved?.(data.storage_path);
        toast.success("Marked-up copy added");
        onClose?.();
      } catch (e) {
        toast.error(fmtErr(e));
      } finally {
        setSaving(false);
      }
    }, "image/jpeg", 0.85);
  };

  if (!path) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose?.(); }}>
      <DialogContent className="max-w-3xl" data-testid="markup-dialog">
        <DialogHeader>
          <DialogTitle>Mark up photo</DialogTitle>
          <DialogDescription>Drag to draw an arrow or circle on the problem spot. Saved as a new copy — the original is kept.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant={tool === "arrow" ? "default" : "outline"} size="sm"
            className={tool === "arrow" ? "bg-ink hover:bg-black" : ""}
            onClick={() => setTool("arrow")} data-testid="markup-tool-arrow">
            <MoveUpRight className="h-4 w-4 mr-1" />Arrow
          </Button>
          <Button type="button" variant={tool === "circle" ? "default" : "outline"} size="sm"
            className={tool === "circle" ? "bg-ink hover:bg-black" : ""}
            onClick={() => setTool("circle")} data-testid="markup-tool-circle">
            <CircleIcon className="h-4 w-4 mr-1" />Circle
          </Button>
          <span className="mx-1 h-5 w-px bg-zinc-200" />
          {COLORS.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)}
              className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-zinc-900 scale-110" : "border-zinc-300"} transition-transform`}
              style={{ backgroundColor: c }} data-testid={`markup-color-${c.slice(1)}`} aria-label={`Color ${c}`} />
          ))}
          <span className="mx-1 h-5 w-px bg-zinc-200" />
          <Button type="button" variant="outline" size="sm" disabled={!shapes.length}
            onClick={() => setShapes((s) => s.slice(0, -1))} data-testid="markup-undo">
            <Undo2 className="h-4 w-4 mr-1" />Undo
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!shapes.length}
            onClick={() => setShapes([])} data-testid="markup-clear">
            <Eraser className="h-4 w-4 mr-1" />Clear
          </Button>
        </div>
        <div className="flex justify-center bg-zinc-900/90 rounded-md p-2">
          {!ready && <div className="h-64 grid place-items-center text-sm text-zinc-400">Loading photo…</div>}
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className={`max-w-full max-h-[55vh] touch-none ${ready ? "" : "hidden"} ${tool ? "cursor-crosshair" : ""}`}
            data-testid="markup-canvas"
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="markup-cancel">Cancel</Button>
          <Button className="bg-ink hover:bg-black" onClick={save} disabled={!shapes.length || saving} data-testid="markup-save">
            {saving ? "Saving…" : "Save marked-up copy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
